/**
 * What a card may total of a list's money (W11 of docs/plans/workbook-map.md,
 * the middle way, narrowed by the owner on 29 Oct 2026). A list may show money
 * it works out from records (Billed, Received and Pending on Customers): the
 * server moves it on every save, edit, delete and undo, and a list syncs
 * whole, so a card may read it over the list's own rows ("Pending by
 * customer", the Pending total). Refused with `LIST_MONEY_NOT_TOTALLED`:
 *
 *   - money typed on a list, and a formula that reads it: nothing works it
 *     out from records, so nothing says it is not counted twice;
 *   - a list's money read through a pick from a record (a job's customer's
 *     Pending): the total would count one customer's Pending once for each
 *     entry that picks them.
 *
 * One reader for every place that asks: propose_card and OS's `total` on the
 * server, a Dashboard card of a setup draft, the card editor on the web, and
 * the cards a device adopts from its setup. Pure.
 */
import { formulaFieldsRead, formulaHopsRead, formulaOf, readFieldSpec, workedResultOf, type FormulaNode } from './formula';
import type { InsightAggNode, InsightRowSource } from './insights';

export const LIST_MONEY_NOT_TOTALLED = 'LIST_MONEY_NOT_TOTALLED' as const;

/** What the reader needs of one field. */
export type ListMoneyField = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  readonly spec?: unknown;
  readonly targetRecordId?: string | null;
};

/**
 * How the reader asks about the business: whether a record is a list, and
 * its fields (null when they are not known, which reads as no money).
 */
export type ListMoneyLookup = {
  readonly isList: (recordId: string) => boolean;
  readonly fields: (recordId: string) => readonly ListMoneyField[] | null;
};

/** Whether a field holds an amount: a money field, or a formula or a total that works one out. */
export const answersMoney = (field: { readonly type: string; readonly spec?: unknown }): boolean =>
  field.type === 'money' || ((field.type === 'formula' || field.type === 'rollup') && workedResultOf(field.type, field.spec) === 'money');

/**
 * Whether a list's money field is typed, or is a formula that reads money
 * typed on its row: not worked out from records. A total (rollup) never is;
 * a formula is when any field it reads, through other formulas, is.
 */
const typedOnList = (field: ListMoneyField, fields: readonly ListMoneyField[], seen: ReadonlySet<string> = new Set()): boolean => {
  if (field.type === 'money') return true;
  if (field.type !== 'formula' || seen.has(field.id)) return false;
  const spec = formulaOf(readFieldSpec(field.spec));
  if (spec === null) return false;
  const next = new Set([...seen, field.id]);
  return formulaFieldsRead(spec).some((fieldId) => {
    const read = fields.find((one) => one.id === fieldId);
    return read !== undefined && typedOnList(read, fields, next);
  });
};

/** The money field of a list that `fieldId` on `recordId` is and a card may not total over the list's rows, or null. */
const listMoneyField = (recordId: string, fieldId: string, lookup: ListMoneyLookup): ListMoneyField | null => {
  if (!lookup.isList(recordId)) return null;
  const fields = lookup.fields(recordId) ?? [];
  const field = fields.find((one) => one.id === fieldId);
  return field !== undefined && answersMoney(field) && typedOnList(field, fields) ? field : null;
};

/** The money field of a list a recipe reads on `recordId`: on its own row typed, or any on a row a hop lands on. */
const listMoneyOfExpr = (recordId: string, expr: FormulaNode, lookup: ListMoneyLookup): ListMoneyField | null => {
  const spec = { result: 'number' as const, onMissing: 'blank' as const, expr };
  for (const fieldId of formulaFieldsRead(spec)) {
    const found = listMoneyField(recordId, fieldId, lookup);
    if (found !== null) return found;
  }
  for (const hop of formulaHopsRead(spec)) {
    const via = lookup.fields(recordId)?.find((one) => one.id === hop.via);
    const there = via?.targetRecordId;
    if (there === undefined || there === null || !lookup.isList(there)) continue;
    // Through the pick: each entry would add its party's money again, worked out or not.
    const field = lookup.fields(there)?.find((one) => one.id === hop.fieldId);
    if (field !== undefined && answersMoney(field)) return field;
  }
  return null;
};

/**
 * The money field of a list one row source adds up on `recordId` and may
 * not, or null. A list's worked-out money over its own rows is open.
 */
export const listMoneyOfSource = (recordId: string, source: InsightRowSource | undefined, lookup: ListMoneyLookup): ListMoneyField | null => {
  if (source === undefined || source.kind === 'measure') return null;
  if (source.kind === 'field') return listMoneyField(recordId, source.fieldId, lookup);
  return listMoneyOfExpr(recordId, source.expr, lookup);
};

/** The money field of a list any total of a card's tree adds up and may not, or null. A leaf with no record reads `hostRecordId`. */
export const listMoneyOfTree = (hostRecordId: string, node: InsightAggNode, lookup: ListMoneyLookup): ListMoneyField | null => {
  switch (node.kind) {
    case 'agg':
      return node.agg === 'rows' || node.agg === 'count' ? null : listMoneyOfSource(node.recordId ?? hostRecordId, node.of, lookup);
    case 'const':
      return null;
    case 'calc':
      return listMoneyOfTree(hostRecordId, node.left, lookup) ?? listMoneyOfTree(hostRecordId, node.right, lookup);
    case 'round':
      return listMoneyOfTree(hostRecordId, node.of, lookup);
  }
};
